import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import {
  DisabilityBenefitPeriod,
  DisabilityDefinition,
  EliminationPeriod,
} from "@shared/schema";

const ELIMINATION_PERIOD_OPTIONS = [
  { value: EliminationPeriod.ZERO_DAYS, label: "0 Days (Immediate)" },
  { value: EliminationPeriod.SEVEN_DAYS, label: "7 Days" },
  { value: EliminationPeriod.FOURTEEN_DAYS, label: "14 Days" },
  { value: EliminationPeriod.THIRTY_DAYS, label: "30 Days" },
  { value: EliminationPeriod.SIXTY_DAYS, label: "60 Days" },
  { value: EliminationPeriod.NINETY_DAYS, label: "90 Days" },
  { value: EliminationPeriod.ONE_HUNDRED_EIGHTY_DAYS, label: "180 Days" },
];

const BENEFIT_PERIOD_OPTIONS = [
  { value: DisabilityBenefitPeriod.THIRTEEN_WEEKS, label: "13 Weeks" },
  { value: DisabilityBenefitPeriod.TWENTY_SIX_WEEKS, label: "26 Weeks" },
  { value: DisabilityBenefitPeriod.TWO_YEARS, label: "2 Years" },
  { value: DisabilityBenefitPeriod.FIVE_YEARS, label: "5 Years" },
  { value: DisabilityBenefitPeriod.TEN_YEARS, label: "10 Years" },
  { value: DisabilityBenefitPeriod.TO_AGE_65, label: "To Age 65" },
  { value: DisabilityBenefitPeriod.TO_AGE_67, label: "To Age 67" },
  { value: DisabilityBenefitPeriod.TO_SOCIAL_SECURITY_RETIREMENT, label: "To Social Security Retirement" },
  { value: DisabilityBenefitPeriod.TO_SSNRA, label: "To SSNRA" },
  { value: DisabilityBenefitPeriod.LIFETIME, label: "Lifetime" },
];

const DISABILITY_DEFINITION_OPTIONS = [
  { value: DisabilityDefinition.OWN_OCCUPATION, label: "Own Occupation", description: "Cannot perform own job duties" },
  { value: DisabilityDefinition.MODIFIED_OWN_OCCUPATION, label: "Modified Own Occupation", description: "Own occupation for period, then any" },
  { value: DisabilityDefinition.ANY_OCCUPATION, label: "Any Occupation", description: "Cannot perform any job" },
];

const BENEFIT_SCHEDULE_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

function FieldLabel({ htmlFor, tooltip, children }: { htmlFor?: string; tooltip: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 mb-1">
      <Label htmlFor={htmlFor} className="mb-0">
        {children}
      </Label>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-sm">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

interface DisabilityTabProps {
  formData: Record<string, any>;
  updateField: (field: string, value: any) => void;
  planType: string;
}

export function DisabilityTab({ formData, updateField, planType }: DisabilityTabProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel htmlFor="eliminationPeriod" tooltip="The waiting period after disability begins before benefits start paying. Longer periods lower premiums but delay income replacement.">Elimination Period</FieldLabel>
          <Select
            value={formData.eliminationPeriod || ""}
            onValueChange={(value) => updateField("eliminationPeriod", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select waiting period" />
            </SelectTrigger>
            <SelectContent>
              {ELIMINATION_PERIOD_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Waiting period before benefits begin
          </p>
        </div>

        <div>
          <FieldLabel htmlFor="maximumBenefitPeriod" tooltip="The longest duration benefits will be paid for a single disability claim. STD is typically weeks; LTD can extend to retirement age.">Maximum Benefit Period</FieldLabel>
          <Select
            value={formData.maximumBenefitPeriod || ""}
            onValueChange={(value) => updateField("maximumBenefitPeriod", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select benefit duration" />
            </SelectTrigger>
            <SelectContent>
              {BENEFIT_PERIOD_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            How long benefits are paid
          </p>
        </div>
      </div>

      <div>
        <FieldLabel htmlFor="disabilityDefinition" tooltip="The legal standard used to determine if someone qualifies as disabled. 'Own Occupation' is more generous (can't do your specific job); 'Any Occupation' is stricter (can't do any job).">Definition of Disability</FieldLabel>
        <Select
          value={formData.disabilityDefinition || ""}
          onValueChange={(value) => updateField("disabilityDefinition", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select definition" />
          </SelectTrigger>
          <SelectContent>
            {DISABILITY_DEFINITION_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div>
                  <div className="font-medium">{option.label}</div>
                  <div className="text-xs text-muted-foreground">{option.description}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Benefit Schedule</Label>
          <Select
            value={formData.benefitSchedule || ""}
            onValueChange={(value) => updateField("benefitSchedule", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select schedule" />
            </SelectTrigger>
            <SelectContent>
              {BENEFIT_SCHEDULE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            {planType === "std" ? "STD is typically paid weekly" : "LTD is typically paid monthly"}
          </p>
        </div>

        <div>
          <FieldLabel htmlFor="maxMonthlyBenefit" tooltip="The dollar cap on benefit payments per period, regardless of the employee's earnings. Limits exposure on high earners.">
            {formData.benefitSchedule === "weekly" ? "Maximum Weekly Benefit" : "Maximum Monthly Benefit"}
          </FieldLabel>
          <Input
            id="maxMonthlyBenefit"
            type="number"
            step="100"
            value={formData.maxMonthlyBenefit || ""}
            onChange={(e) => updateField("maxMonthlyBenefit", e.target.value)}
            placeholder="10000"
          />
          <p className="text-xs text-muted-foreground mt-1">
            {formData.benefitSchedule === "weekly" ? "Maximum weekly payout amount" : "Maximum monthly payout amount"}
          </p>
        </div>
      </div>
    </div>
  );
}
